import {
  Button,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
} from '@chakra-ui/react';
import { SearchIcon, UserAddIcon } from '@heroicons/react/outline';
import { useTranslations } from 'use-intl';

type Props = {
  /* ** Callback for the search input field ** */
  onSearchInputChange: (input: string) => void;
  /* ** Callback on new subscription ** */
  onCreateSubscription: () => void;
  /* ** Callback on the CSV file import ** */
  onImportSubscriptions: () => void;
};

/**
 * SubscribersTableControls.
 * Controls section of the `<SubscribersTable />`. Add subscriber and import CSV.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscribersTableControls({
  onSearchInputChange,
  onCreateSubscription,
  onImportSubscriptions,
}: Props): JSX.Element {
  const i18nSubscribersTableControls = useTranslations('SubscribersTableControls');

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onSearchInputChange(event.target.value);
  };

  const handleCreateSubscription = (): void => {
    onCreateSubscription();
  };

  const handleImportSubscriptions = (): void => {
    onImportSubscriptions();
  };

  return (
    <Stack>
      <Heading size="md">{i18nSubscribersTableControls('Title')}</Heading>
      <Stack direction={{ base: 'column', md: 'row' }} justifyContent="space-between" spacing="4">
        <InputGroup w={{ base: 'full', md: '300px' }}>
          <InputLeftElement pointerEvents="none">
            <Icon as={SearchIcon} boxSize="18px" />
          </InputLeftElement>
          <Input
            placeholder={i18nSubscribersTableControls('SearchPlaceholder')}
            onChange={handleSearchInputChange}
          />
        </InputGroup>
        <Stack direction={{ base: 'column', md: 'row' }} justifyContent="space-between">
          <Button
            data-cy="add-subscriber"
            leftIcon={<Icon as={UserAddIcon} boxSize="24px" />}
            width={{ base: 'fit-content' }}
            onClick={handleCreateSubscription}
          >
            {i18nSubscribersTableControls('AddSubscriber')}
          </Button>
          <Button variant="secondary" onClick={handleImportSubscriptions}>
            {i18nSubscribersTableControls('ImportCSV')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

export type SubscribersTableControlsProps = Props;
